/* resumeMintFlow.js — v6.51.11 (2026-02-20)
 *
 * One-function controller for the "Retry Mint (preserves serials)" button
 * added to the Advanced Support drawer.  Replaces the owner-rejected
 * JavaScript bookmarklet with an in-UI, same-session action.
 *
 * Design constraints (per owner authorization R1-A):
 *   • Target one order at a time, by order_id. No new serial / license /
 *     order allocation. The server-side `/resume-mint` endpoint guarantees
 *     this via `**d` spread preservation at usb_orders.py:1734-1743.
 *   • Open Gate 3 only for the time needed to dispatch, with the backend's
 *     minimum valid TTL (1 h). The handler closes Gate 3 as soon as it
 *     confirms every allocated drive has a mint_job_id.
 *   • "Dispatched" means status === "building" AND driveCount > 0 AND
 *     dispatchedCount === driveCount (every allocated slot carries a
 *     mint_job_id). Order-status alone is NOT enough.
 *     v6.51.11 — previously hardcoded `=== 4` from the PS 149 recovery,
 *     which false-timed-out every order with ≠4 drives (e.g. the
 *     Urban Scholars 1-drive K-5 order, 2026-10-27).
 *   • Gate 3 closure is reported CLOSED only when:
 *       (a) POST /gate3/close returns ok with a non-null body, AND
 *       (b) GET /gate3/status returns ok with body.open === false AND
 *           body.state === "closed".
 *     Any other outcome is reported UNKNOWN (never silently success).
 *   • finally{} always attempts to close Gate 3 if it was opened — even on
 *     dispatch failure, timeout, or exception, so the gate never stays open
 *     past the brief dispatch window.
 *   • No credential / token logging. The injected transport is called with
 *     `credentials: 'include'` by the React caller; this module never reads
 *     or forwards cookies, bearer tokens, or Authorization headers.
 *
 * Shape:
 *   runResumeMintFlow({ orderId, transport, onProgress, config? }) → Promise<Summary>
 *     Summary = { orderId, dispatchConfirmed, closeAttempted,
 *                 closeOutcome: 'CLOSED'|'STILL_OPEN'|'UNKNOWN'|'N/A',
 *                 closeDetail, log: Array<string>,
 *                 serials: string[], driveCount: number,
 *                 dispatchedCount: number, error?: string }
 *
 *   transport({ method, path, body? }) → Promise<{ status, ok, body }>
 *     Pure interface — unit-testable with a plain mock.  The caller injects a
 *     fetch-based implementation that uses `credentials:'include'`.
 */

export const DEFAULT_CONFIG = Object.freeze({
  gate3TtlHours: 1,            // backend validator floor
  pollIntervalMs: 2000,
  pollMaxMs: 60000,
  scope: null,                 // caller supplies order-specific text
});

export async function runResumeMintFlow({
  orderId,
  transport,
  onProgress = () => {},
  config = {},
}) {
  if (!orderId) throw new Error('orderId is required');
  if (typeof transport !== 'function') throw new Error('transport is required');

  const cfg = { ...DEFAULT_CONFIG, ...config };
  const log = [];
  const push = (level, msg, extra) => {
    const line =
      `[${new Date().toISOString()}] ${level} ${msg}` +
      (extra !== undefined ? ' ' + safeStringify(extra) : '');
    log.push(line);
    try { onProgress({ level, msg, extra, line }); } catch (_) {}
  };

  let opened = false;
  let dispatchConfirmed = false;
  let closeOutcome = 'UNKNOWN';
  let closeDetail = null;
  let serials = [];
  let driveCount = 0;
  let dispatchedCount = 0;
  let error = undefined;

  const scope =
    cfg.scope ||
    `Retry mint for ${orderId} — Owner authorized (via Advanced Support)`;

  try {
    push('STEP1', `Opening Gate 3 (ttl_hours=${cfg.gate3TtlHours})`);
    const op = await safeCall(transport, {
      method: 'POST',
      path: '/governance/gate3/open',
      body: { scope, ttl_hours: cfg.gate3TtlHours },
    });
    push('STEP1', 'Gate 3 open response', { status: op.status, ok: op.ok });
    if (!op.ok) {
      error = `Gate 3 open failed: HTTP ${op.status}`;
      throw new Error(error);
    }
    opened = true;

    push('STEP2', `POST /usb-orders/${orderId}/resume-mint`);
    const rm = await safeCall(transport, {
      method: 'POST',
      path: `/usb-orders/${orderId}/resume-mint`,
    });
    push('STEP2', 'Resume-mint response', { status: rm.status, ok: rm.ok });
    if (!rm.ok) {
      error = `Resume-mint failed: HTTP ${rm.status}`;
      throw new Error(error);
    }

    push('STEP3', 'Polling for BUILDING + all drives dispatched');
    const t0 = Date.now();
    while (Date.now() - t0 < cfg.pollMaxMs) {
      await sleep(cfg.pollIntervalMs);
      const g = await safeCall(transport, {
        method: 'GET',
        path: `/usb-orders/${orderId}`,
      });
      const ord = extractOrder(g.body);
      const status = ord && ord.status;
      const drives = ord && Array.isArray(ord.drives) ? ord.drives : [];
      serials = drives.map((d) => d && d.serial).filter(Boolean);
      driveCount = drives.length;
      dispatchedCount = drives.filter((d) => d && d.mint_job_id).length;
      push('STEP3', 'Poll', {
        status, drives: driveCount, dispatched: dispatchedCount, serials,
      });
      if (status === 'gate3_blocked') {
        error = 'Order reverted to gate3_blocked';
        throw new Error(error);
      }
      // v6.51.11 (2026-02-20) — Confirm when the server reports the
      // order has transitioned to ``building`` AND every allocated
      // drive carries a ``mint_job_id`` (i.e. the dispatcher has
      // handed off every slot). The previous version hardcoded "4"
      // from the PS 149 K-5 recovery and would false-negative every
      // order with ≠4 drives — a 1-drive Urban Scholars order would
      // flash "Mint NOT dispatched" after 60s even though drive 1/1
      // was dispatched on the first poll.
      if (
        status === 'building' &&
        driveCount > 0 &&
        dispatchedCount === driveCount
      ) {
        dispatchConfirmed = true;
        break;
      }
    }
    if (!dispatchConfirmed) {
      error =
        `Timeout after ${cfg.pollMaxMs}ms: did not observe status=building ` +
        `with every drive dispatched. Last poll: ${dispatchedCount}/${driveCount} ` +
        `drives dispatched.`;
      throw new Error(error);
    }
    push(
      'STEP3',
      `✅ ${dispatchedCount} mint job(s) dispatched. ` +
      'Manufacturing IN FLIGHT, NOT complete.'
    );
  } catch (e) {
    if (!error) error = e && e.message ? e.message : String(e);
    push('ERROR', error);
  } finally {
    if (opened) {
      const result = await attemptCloseAndVerify({
        transport, cfg, push, dispatchConfirmed,
      });
      closeOutcome = result.outcome;
      closeDetail = result.detail;
    } else {
      push('INFO', 'Gate 3 never opened — no close needed.');
      closeOutcome = 'N/A';
    }
  }

  return {
    orderId,
    dispatchConfirmed,
    closeAttempted: opened,
    closeOutcome,
    closeDetail,
    log,
    serials,
    driveCount,
    dispatchedCount,
    error,
  };
}

async function attemptCloseAndVerify({ transport, cfg, push, dispatchConfirmed }) {
  const closeReason = dispatchConfirmed
    ? `${cfg.scope || 'retry'} — mint dispatched, closing`
    : `${cfg.scope || 'retry'} — resume failed, closing`;

  const cl = await safeCall(transport, {
    method: 'POST',
    path: '/governance/gate3/close',
    body: { reason: closeReason },
  });
  push('STEP4', 'Close response', { status: cl.status, ok: cl.ok, threw: cl.threw });

  const stt = await safeCall(transport, {
    method: 'GET',
    path: '/governance/gate3/status',
  });
  push('STEP4', 'Gate 3 status after close', {
    status: stt.status, ok: stt.ok, body: stt.body, threw: stt.threw,
  });

  const closeOk = cl.ok && cl.body !== null && cl.body !== undefined;
  const statusOk =
    stt.ok && stt.body !== null && stt.body !== undefined &&
    typeof stt.body === 'object';
  const hasOpenField = statusOk && typeof stt.body.open === 'boolean';
  const hasStateField = statusOk && typeof stt.body.state === 'string';

  if (
    closeOk && statusOk && hasOpenField && hasStateField &&
    stt.body.open === false && stt.body.state === 'closed'
  ) {
    push('STEP4', '✅ Gate 3 CLOSED — verified by /status endpoint.');
    return {
      outcome: 'CLOSED',
      detail: { closeStatus: cl.status, verifyStatus: stt.status },
    };
  }
  if (closeOk && statusOk && hasOpenField && stt.body.open === true) {
    push(
      'WARN',
      `⚠️ Close returned OK but /status reports open=true. ` +
      `Gate 3 will auto-expire at TTL (${cfg.gate3TtlHours}h). ` +
      `Verify manually in the Gate 3 strip.`
    );
    return {
      outcome: 'STILL_OPEN',
      detail: {
        closeStatus: cl.status,
        verifyStatus: stt.status,
        statusBody: stt.body,
      },
    };
  }
  push(
    'WARN',
    `⚠️ Closure UNKNOWN: the close call or /status did not meet the ` +
    `expected contract (ok + body.open=false + body.state="closed"). ` +
    `Gate 3 may remain open until TTL (${cfg.gate3TtlHours}h). ` +
    `Verify manually in the Gate 3 strip and close via that strip if open.`
  );
  return {
    outcome: 'UNKNOWN',
    detail: {
      closeOk, closeStatus: cl.status, closeThrew: cl.threw,
      verifyOk: stt.ok, verifyStatus: stt.status, verifyThrew: stt.threw,
      hasOpenField, hasStateField, statusBody: stt.body,
    },
  };
}

// ─── helpers ──────────────────────────────────────────────────────────

function extractOrder(body) {
  if (!body) return null;
  if (body.order && typeof body.order === 'object') return body.order;
  return body;
}

async function safeCall(transport, req) {
  try {
    const resp = await transport(req);
    if (!resp || typeof resp !== 'object') {
      return { status: 0, ok: false, body: null, threw: true, err: 'bad-response' };
    }
    return { ...resp, threw: false };
  } catch (e) {
    return {
      status: 0, ok: false, body: null, threw: true,
      err: (e && e.message) || String(e),
    };
  }
}

function sleep(ms) { return new Promise((r) => setTimeout(r, ms)); }

function safeStringify(v) {
  try { return JSON.stringify(v); } catch (_) { return String(v); }
}
